import { ProductForm, mapProductToFormDefaults } from "@bcc/components/shopify-import/product-form";
import { createProductSchema, type ProductFormData } from "@bcc/components/shopify-import/schemas/product-schema";
import { Alert, AlertDescription } from "@bcc/components/ui/alert";
import type { IntensityComparisonCoffee } from "@bcc/components/ui/intensity-scale";
import { Button } from "@bcc/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import { getVendorLocaleMetadata } from "@bcc/components/views/products-view.utils";
import { useFeatureFlagsContext } from "@bcc/contexts/feature-flags-context";
import { useTaxRatesContext } from "@bcc/contexts/tax-rates-context";
import { homeRegionForVendor } from "@bcc/lib/products/cross-border-tax";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { SalesforceProduct } from "@shared/services/salesforce/types";
import { createSsRoasterSku } from "@client-shared/services/api/ss-roaster-products";
import { zodResolver } from "@hookform/resolvers/zod";
import { FEATURE_FLAG_NAMES, type FormProduct, type LocalisedProductConfig, type ProductConfig } from "@shared/types";
import { isIntensityPosition } from "@shared/types/intensity";
import { AlertCircle, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ReviewSuccess } from "./shopify-import-modal/components/review-success/review-success";
import { SUCCESS_CLOSE_TIMEOUT_MS } from "./shopify-import-modal/shopify-import-modal";
import {
  EMPTY_SS_PRODUCT,
  mapFormDataToDraftProduct,
  mapFormToSalesforcePayloads,
  setImageUploadsFromProductImages,
  setSsSkuOnboardingModalTestId,
} from "./ss-sku-onboarding-modal.utils";

// Re-export so callers/tests can keep importing from this module.
export { SUCCESS_CLOSE_TIMEOUT_MS };

interface SsSkuOnboardingModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmitted: () => void;
  readonly testId?: string;
  readonly productConfig?: ProductConfig;
  readonly localisedProductConfig?: LocalisedProductConfig;
  readonly shortTermMinWeeklyInventory?: number;
  readonly shortTermMaxWeeklyInventory?: number;
  readonly longTermMinWeeklyInventory?: number;
  readonly longTermMaxWeeklyInventory?: number;
  readonly roasterNoteMaxLength?: number;
  readonly productDescriptionMaxLength?: number;
  readonly titleMaxLength?: number;
  readonly regionLargeBagSize?: string[];
  /** When provided, the modal opens in "New Bag Size" mode with product fields
   *  pre-filled and read-only. Only variant fields are editable. */
  readonly existingProduct?: FormProduct;
  /** Existing product names for duplicate name validation on New SKU mode. */
  readonly existingProductNames?: string[];
  readonly marginFloorPercent?: number;
  /** The roaster's other scored coffees, shown as comparison markers on the
   *  intensity scale. Without this the ShipStation flow always reports "no
   *  other scored coffees" even when the catalog has them. */
  readonly comparisonCoffees?: readonly IntensityComparisonCoffee[];
  /** When provided (with a `serviceId`, the Salesforce `SKU_Onboarding_Request__c`
   *  record Id), the modal opens in "Resubmit" mode: fields are pre-filled from
   *  the rejected request but fully editable (unlike `existingProduct`'s
   *  read-only New Bag Size mode), and submitting sends `serviceId` back as
   *  `Id` in the same create payload/endpoint so Salesforce updates the
   *  existing record in place instead of creating a new one. */
  readonly resubmitProduct?: FormProduct;
  /** When provided, the modal opens resuming a previously saved draft: fields
   *  are pre-filled as-is and fully editable (same leniency as resubmit —
   *  image optional, no read-only fields), but submitting goes through the
   *  normal create path (not the resubmit PATCH-style upsert), since a draft
   *  is just a saved-for-later New SKU / New Bag Size form, not a Salesforce
   *  record yet. */
  readonly draftProduct?: FormProduct;
  /** Called with the current in-progress form contents when the roaster
   *  clicks "Save Draft". The parent owns persistence (ProductsProvider's
   *  sku-import-draft mechanism), this modal just reports the snapshot. May
   *  return a promise — the Save Draft button stays disabled until it
   *  settles. */
  readonly onSaveDraft?: (product: FormProduct) => void | Promise<void>;
  /** Called once a draft has been successfully submitted, so the parent can
   *  mark the originating draft entry PUBLISHED and drop it from the drafts list. */
  readonly onDraftPublished?: (product: FormProduct) => void;
  /** Whether the coffee being submitted already exists in PIM, scored or not.
   *  Intensity is set once per coffee, so it is locked and its keys omitted
   *  from the payload. New Bag Size mode implies it; resubmit and resumed
   *  drafts have no `localVariants`, so the caller looks it up in PIM. */
  readonly isCoffeeOnPim?: boolean;
  /** True when the PIM match rests on a shared product id, so renaming cannot unlock it. */
  readonly isCoffeeIdentifiedByPimId?: boolean;
}

function listMissingSubmitFields(fields: Readonly<Record<string, string | null | undefined>>): string {
  return Object.entries(fields)
    .filter(([, value]) => !value)
    .map(([name]) => name)
    .join(", ");
}

async function submitSsSkuPayloads(payloads: SalesforceProduct[], accessToken: string, resubmitServiceId?: string): Promise<void> {
  if (!resubmitServiceId) {
    await createSsRoasterSku(payloads, accessToken);
    return;
  }
  const [payload] = payloads;
  if (!payload) throw new Error("Unable to build resubmit payload");
  // Send the rejected record's Salesforce Id back as `Id`, through the
  // same create endpoint/payload used for a new SKU — the Apex listener
  // matches on `Id` and updates the existing record in place instead of
  // creating a new one. External_Product_ID__c is create-only (the
  // mapper mints a fresh `SS-<uuid>` for new records) and Salesforce
  // never needs it on a resubmit, so strip it rather than overwrite the
  // record's existing value with the freshly minted one.
  const { External_Product_ID__c: _mintedExternalId, ...resubmitPayload } = payload;
  await createSsRoasterSku({ ...resubmitPayload, Id: resubmitServiceId }, accessToken);
}

export function SsSkuOnboardingModal({
  open,
  onOpenChange,
  onSubmitted,
  testId,
  productConfig,
  localisedProductConfig,
  shortTermMinWeeklyInventory,
  shortTermMaxWeeklyInventory,
  longTermMinWeeklyInventory,
  longTermMaxWeeklyInventory,
  roasterNoteMaxLength,
  productDescriptionMaxLength,
  titleMaxLength,
  regionLargeBagSize,
  existingProduct,
  existingProductNames = [],
  marginFloorPercent,
  comparisonCoffees,
  resubmitProduct,
  draftProduct,
  onSaveDraft,
  onDraftPublished,
  isCoffeeOnPim = false,
  isCoffeeIdentifiedByPimId = false,
}: SsSkuOnboardingModalProps) {
  const { t } = useTranslation();
  const [authState] = useSFDCAuthState();
  const [{ currentVendor, currencyObject }] = useVendorContext();
  const ratesByRegion = useTaxRatesContext();
  const { isFeatureEnabled } = useFeatureFlagsContext();
  const isBadgesEnabled = isFeatureEnabled(FEATURE_FLAG_NAMES.badges);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [submissionState, setSubmissionState] = useState<"idle" | "success">("idle");
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSuccessTimeout = useCallback(() => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, []);

  const isNewBagSizeMode = Boolean(existingProduct);
  const isResubmitMode = Boolean(resubmitProduct?.serviceId);
  const isDraftMode = Boolean(draftProduct) && !isResubmitMode;

  // Draft-in-progress id: seeded from the resumed draft's id (see the reset
  // effect below), or lazily minted on the first Save Draft click for a brand
  // new session — so repeated saves within the same session update the same
  // draft entry instead of creating duplicates.
  const draftIdRef = useRef<string | undefined>(draftProduct?.id);

  // Bag sizes already imported on the selected SKU — filtered out of the
  // variant dropdown so a roaster adding a new bag size can only pick a size
  // that isn't already on the SKU. Existing variants are dropped from the
  // form's `variants` array (see `initialProduct` below), so we capture the
  // list here before that happens.
  const excludedBagSizes = useMemo<readonly string[]>(() => {
    if (!existingProduct?.variants?.length) return [];
    return Array.from(
      new Set(existingProduct.variants.map((variant) => variant.bagSize).filter((bagSize): bagSize is string => Boolean(bagSize)))
    );
  }, [existingProduct]);

  // In new bag size mode, use the existing product with a fresh empty variant.
  // The catalog feed populates `images` (PIM `src` URLs) but the ProductForm
  // renders from `imageUploads`, so hydrate it here when missing so the parent
  // SKU's image surfaces in the read-only image slot.
  //
  // In resubmit mode, prefill from the rejected request as-is (fields stay
  // fully editable — no read-only fields and no blank variant swap-in).
  const initialProduct = useMemo(() => {
    if (resubmitProduct) {
      const imageUploads = resubmitProduct.imageUploads?.length
        ? resubmitProduct.imageUploads
        : setImageUploadsFromProductImages(resubmitProduct.images);
      return { ...resubmitProduct, imageUploads };
    }
    if (draftProduct) {
      const imageUploads = draftProduct.imageUploads?.length
        ? draftProduct.imageUploads
        : setImageUploadsFromProductImages(draftProduct.images);
      return { ...draftProduct, imageUploads };
    }
    if (!existingProduct) return EMPTY_SS_PRODUCT;
    const imageUploads = existingProduct.imageUploads?.length
      ? existingProduct.imageUploads
      : setImageUploadsFromProductImages(existingProduct.images);
    return {
      ...existingProduct,
      imageUploads,
      // Persisted with a saved draft, so reopening a new bag-size draft (which
      // arrives as `draftProduct` alone) still knows the score is established.
      hasApprovedIntensity: existingProduct.hasApprovedIntensity ?? isIntensityPosition(existingProduct.intensityPosition),
      variants: [
        {
          type: "variant" as const,
          id: `ss-variant-${globalThis.crypto.randomUUID().slice(0, 8)}`,
          bagSize: "",
          grind: [],
          price: "",
          wholesalePrice: "",
          term: "Long" as const,
          startDate: "",
          isProductSelectedForImport: true,
        },
      ],
    };
  }, [existingProduct, resubmitProduct, draftProduct]);

  // In New Bag Size mode the image slot is read-only, so the roaster can never
  // select an image themselves — `mapProductToFormDefaults` would leave the
  // `imageUploads` form field null (its locked-product predicate doesn't know
  // about the `readOnlyProductFields` prop) and the parent SKU's image would be
  // shown in the UI but silently dropped from the Salesforce payload. Seed the
  // hydrated image as the form selection so it reaches `Image_Doc__c`.
  const formDefaults = useMemo(() => {
    const defaults = mapProductToFormDefaults(initialProduct);
    if (isNewBagSizeMode && !defaults.imageUploads) {
      return { ...defaults, imageUploads: initialProduct.imageUploads ?? null };
    }
    return defaults;
  }, [initialProduct, isNewBagSizeMode]);

  const schema = useMemo(
    () =>
      createProductSchema({
        imageOptional: isNewBagSizeMode || isResubmitMode || isDraftMode,
        // Bag-size mode locks product-level fields to the parent SKU's values —
        // validating their content would let a sparse parent (e.g. PIM-only,
        // missing flavor/roast data) permanently disable submit.
        readOnlyProductFields: isNewBagSizeMode,
        shortTermMinWeeklyInventory,
        shortTermMaxWeeklyInventory,
        longTermMinWeeklyInventory,
        longTermMaxWeeklyInventory,
        enforceMarginFloor: true,
        marginFloorPercent,
        // Must match the limit ProductForm renders in its character counter,
        // otherwise the counter and this resolver disagree.
        titleMaxLength,
        // Must match ProductForm's own isBadgesEnabled — that's what gates
        // whether the Badges field is even rendered for the roaster to fix.
        badgesEnabled: isBadgesEnabled,
      }),
    [
      isNewBagSizeMode,
      isResubmitMode,
      isDraftMode,
      shortTermMinWeeklyInventory,
      shortTermMaxWeeklyInventory,
      longTermMinWeeklyInventory,
      longTermMaxWeeklyInventory,
      marginFloorPercent,
      titleMaxLength,
      isBadgesEnabled,
    ]
  );

  const form = useForm<ProductFormData>({
    resolver: zodResolver(schema),
    defaultValues: formDefaults,
    mode: "onBlur",
  });

  // Reset form when existingProduct changes (e.g. modal re-opens with different product).
  // Also re-seed the draft-in-progress id for the new session: the resumed
  // draft's id when resuming, or cleared (to be lazily minted) otherwise.
  useEffect(() => {
    form.reset(formDefaults);
    draftIdRef.current = draftProduct?.id;
  }, [formDefaults, form, draftProduct]);

  // Reset all modal state when closed so a reopen starts clean, even when
  // the parent flips `open` directly (bypassing handleClose).
  useEffect(() => {
    if (!open) {
      clearSuccessTimeout();
      setSubmissionState("idle");
      setError(null);
      setLoading(false);
      setSavingDraft(false);
      setIsFormValid(false);
      form.reset(formDefaults);
    }
  }, [open, clearSuccessTimeout, form, formDefaults]);

  // Clear any pending auto-close on unmount so the callback can't fire detached
  useEffect(() => clearSuccessTimeout, [clearSuccessTimeout]);

  const handleClose = useCallback(() => {
    if (loading || submissionState === "success") return;
    form.reset(formDefaults);
    setError(null);
    setIsFormValid(false);
    onOpenChange(false);
  }, [loading, submissionState, form, onOpenChange, formDefaults]);

  // Check for duplicate product name reactively (only in new SKU mode)
  const watchedTitle = form.watch("title");
  const duplicateNameError = useMemo(() => {
    if (isNewBagSizeMode || existingProductNames.length === 0) return null;
    const currentTitle = watchedTitle?.trim().toLowerCase();
    if (!currentTitle) return null;
    // A resubmitted product's own original title is expected to match itself —
    // only renaming onto a *different* existing product is a collision.
    if (isResubmitMode && currentTitle === resubmitProduct?.title?.trim().toLowerCase()) return null;
    const isDuplicate = existingProductNames.some((name) => name.trim().toLowerCase() === currentTitle);
    return isDuplicate ? t("ssRoaster.skuOnboarding.errors.duplicateProductName") : null;
  }, [isNewBagSizeMode, isResubmitMode, resubmitProduct, existingProductNames, watchedTitle, t]);

  // PIM presence was looked up from the title the coffee opened with. A resubmit
  // or resumed draft keeps its title editable, so renaming a coffee that was
  // matched to PIM by title alone makes it a different coffee, and its
  // intensity unlocks. A coffee matched by product id stays the PIM coffee
  // whatever it is called, so it stays locked.
  // (Renaming onto another PIM coffee is already blocked by the duplicate-name
  // error above.) New Bag Size mode locks the title, so it never gets here.
  const openedTitle = (resubmitProduct ?? draftProduct)?.title?.trim().toLowerCase();
  const currentTitle = watchedTitle?.trim().toLowerCase();
  const isTitleRenamed = !isNewBagSizeMode && Boolean(openedTitle) && Boolean(currentTitle) && currentTitle !== openedTitle;
  const isCurrentCoffeeOnPim = isCoffeeOnPim && (isCoffeeIdentifiedByPimId || !isTitleRenamed);

  // Snapshots the current (possibly incomplete/invalid) form contents as a
  // draft. No validation gate — that's the point of a draft. Reuses the same
  // draft id across saves within a session (see draftIdRef above) so repeat
  // clicks update the existing draft entry instead of creating duplicates.
  // The button stays disabled until the parent's persistence call settles.
  const handleSaveDraft = useCallback(async () => {
    if (!onSaveDraft || savingDraft) return;
    if (!draftIdRef.current) {
      draftIdRef.current = `ss-draft-${globalThis.crypto.randomUUID().slice(0, 8)}`;
    }
    setSavingDraft(true);
    try {
      await onSaveDraft(mapFormDataToDraftProduct(initialProduct, form.getValues(), draftIdRef.current));
    } finally {
      setSavingDraft(false);
    }
  }, [onSaveDraft, savingDraft, form, initialProduct]);

  const handleSubmit = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    // Block submission if product name already exists
    if (duplicateNameError) {
      setError(duplicateNameError);
      return;
    }

    const accessToken = authState.sfdcAuthToken;
    const userId = currentVendor?.userId;
    const vendorRegion = currentVendor?.region;
    const currencyIso = currencyObject?.iso;

    if (!accessToken || !userId || !vendorRegion || !currencyIso) {
      const missing = listMissingSubmitFields({ accessToken, userId, vendorRegion, currencyIso });
      setError(`${t("ssRoaster.skuOnboarding.errors.submitFailed") as string} (missing: ${missing})`);
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const formData = form.getValues();
      // For a new bag size we carry the parent SKU's copy-editing fields over,
      // so we need the region's locale config to resolve default/English copy.
      // The standard new-SKU path leaves these fields blank and skips it.
      const regionLocales = isNewBagSizeMode
        ? getVendorLocaleMetadata({ region: vendorRegion, countryCode: currentVendor?.countryCode }).locales
        : [];
      const homeRegion = homeRegionForVendor(currentVendor?.countryCode, currentVendor?.region);
      const payloads = await mapFormToSalesforcePayloads(formData, userId, vendorRegion, currencyIso, isNewBagSizeMode, {
        localizedFields: existingProduct?.localizedFields,
        regionLocales,
        homeRegion,
        ratesByRegion,
        isIntensityEstablished:
          isNewBagSizeMode ||
          isCurrentCoffeeOnPim ||
          Boolean(resubmitProduct?.hasApprovedIntensity) ||
          Boolean(draftProduct?.hasApprovedIntensity),
      });

      await submitSsSkuPayloads(payloads, accessToken, isResubmitMode ? resubmitProduct?.serviceId : undefined);

      form.reset(formDefaults);
      setSubmissionState("success");

      // Resuming a draft and submitting it successfully consumes the draft —
      // mark it PUBLISHED so it drops out of the drafts list.
      if (isDraftMode && draftProduct) {
        onDraftPublished?.(draftProduct);
      }

      clearSuccessTimeout();
      successTimeoutRef.current = globalThis.setTimeout(() => {
        successTimeoutRef.current = null;
        onSubmitted();
        onOpenChange(false);
      }, SUCCESS_CLOSE_TIMEOUT_MS);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`${t("ssRoaster.skuOnboarding.errors.submitFailed") as string} — ${message}`);
    } finally {
      setLoading(false);
    }
  }, [
    form,
    isCurrentCoffeeOnPim,
    authState.sfdcAuthToken,
    currentVendor,
    currencyObject,
    ratesByRegion,
    onSubmitted,
    onOpenChange,
    duplicateNameError,
    isNewBagSizeMode,
    isResubmitMode,
    isDraftMode,
    resubmitProduct,
    draftProduct,
    onDraftPublished,
    formDefaults,
    existingProduct,
    t,
    clearSuccessTimeout,
  ]);

  // Watch all form changes and silently check validity via safeParse
  // (mode: "onBlur" means formState.isValid won't update until every field is blurred)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form's form.watch is a subscription API not analyzable by React Compiler
    const subscription = form.watch(() => {
      const result = schema.safeParse(form.getValues());
      setIsFormValid(result.success);
    });
    return () => subscription.unsubscribe();
  }, [form, schema]);

  // Guard: if productConfig / localisedProductConfig not available yet, show loading
  if (!productConfig || !localisedProductConfig) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          testId={testId ?? setSsSkuOnboardingModalTestId("content")}
          className="sm:max-w-[922px] max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  let modalTitle = t("ssRoaster.skuOnboarding.title");
  let modalDescription = t("ssRoaster.skuOnboarding.description");
  if (isResubmitMode) {
    modalTitle = t("ssRoaster.skuOnboarding.resubmit.title");
    modalDescription = t("ssRoaster.skuOnboarding.resubmit.description");
  } else if (isNewBagSizeMode) {
    modalTitle = t("ssRoaster.newBagSize.modalTitle");
    modalDescription = t("ssRoaster.newBagSize.modalDescription", { productName: existingProduct?.title ?? "" });
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent testId={testId ?? setSsSkuOnboardingModalTestId("content")} className="sm:max-w-[922px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid={setSsSkuOnboardingModalTestId("title")}>{modalTitle}</DialogTitle>
          <DialogDescription>{modalDescription}</DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {submissionState === "success" ? (
            <ReviewSuccess
              title={t("ssRoaster.skuOnboarding.success.title")}
              description={t("ssRoaster.skuOnboarding.success.description")}
              redirectingText={t("ssRoaster.skuOnboarding.success.redirecting")}
            />
          ) : (
            <>
              <ProductForm
                product={initialProduct}
                form={form}
                productConfig={productConfig}
                localisedProductConfig={localisedProductConfig}
                readOnlyProductFields={isNewBagSizeMode}
                isCoffeeOnPim={isCurrentCoffeeOnPim}
                imageOptional={isNewBagSizeMode || isResubmitMode || isDraftMode}
                allowAddRemoveVariants={true}
                titleError={duplicateNameError}
                index={0}
                shortTermMinWeeklyInventory={shortTermMinWeeklyInventory}
                shortTermMaxWeeklyInventory={shortTermMaxWeeklyInventory}
                longTermMinWeeklyInventory={longTermMinWeeklyInventory}
                longTermMaxWeeklyInventory={longTermMaxWeeklyInventory}
                roasterNoteMaxLength={roasterNoteMaxLength}
                productDescriptionMaxLength={productDescriptionMaxLength}
                titleMaxLength={titleMaxLength}
                regionLargeBagSize={regionLargeBagSize}
                excludedBagSizes={excludedBagSizes}
                comparisonCoffees={comparisonCoffees}
              />

              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </>
          )}
        </div>

        {submissionState !== "success" && (
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={loading} data-testid={setSsSkuOnboardingModalTestId("cancel-button")}>
              {t("common.cancel")}
            </Button>
            {!isResubmitMode && onSaveDraft && (
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={loading || savingDraft}
                data-testid={setSsSkuOnboardingModalTestId("save-draft-button")}
              >
                {savingDraft && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("ssRoaster.skuOnboarding.saveDraft")}
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={loading || !isFormValid || !!duplicateNameError}
              data-testid={setSsSkuOnboardingModalTestId("submit-button")}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isResubmitMode ? t("ssRoaster.skuOnboarding.resubmit.submit") : t("ssRoaster.skuOnboarding.submit")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
